// NICE CXone chat (replaces the Intercom bubble behind REACT_APP_ENABLE_NICE_CHAT).
//
// Intercom is injected by GTM, not by this app. With the flag on, public/index.html
// defines a no-op window.Intercom before GTM loads; GTM's Intercom loader sees an
// existing function and skips fetching the widget. That is why the flag is
// build-time: it has to be known before GTM runs.

const DEFAULT_LOADER_URL =
  'https://web-modules-de-na1.niceincontact.com/loader/1/loader.js';

export function getNiceChatConfig() {
  return {
    brandId: process.env.REACT_APP_NICE_BRAND_ID,
    entryPoint: process.env.REACT_APP_NICE_ENTRY_POINT,
    loaderUrl: process.env.REACT_APP_NICE_LOADER_URL || DEFAULT_LOADER_URL
  };
}

// Flag on alone is not enough: without a brand and entry point the bubble
// would open nothing, so stay hidden and let the flag-off path stand.
export function isNiceChatEnabled() {
  const { brandId, entryPoint } = getNiceChatConfig();
  return (
    process.env.REACT_APP_ENABLE_NICE_CHAT === '1' && !!brandId && !!entryPoint
  );
}

const BRAND = '#1a0d88';
const BRAND_2 = '#2d23b5';

// One rule set per call: CXone replaces, not merges, on each setCustomCss.
const CHAT_CSS =
  '[data-selector="ACTIVITY_BAR"] { display: none !important; }' +
  `[data-selector="HEADER"] { background: linear-gradient(90deg, ${BRAND} 0%, ${BRAND_2} 100%) !important; }` +
  `[data-selector="CUSTOMER_MESSAGE_BUBBLE"] { background: ${BRAND} !important; color: #ffffff !important;` +
  ' border: 0 !important; border-radius: 16px 16px 4px 16px !important; box-shadow: none !important; }' +
  '[data-selector="AGENT_MESSAGE_BUBBLE"] { background: #f1f2f6 !important; color: #111827 !important;' +
  ' border: 0 !important; border-radius: 16px 16px 16px 4px !important; box-shadow: none !important; }' +
  `[data-selector="SEND_BUTTON"] { color: ${BRAND} !important; }` +
  `[data-selector="SEND_BUTTON"] svg { fill: ${BRAND} !important; }` +
  // Links the customer pastes would be navy-on-navy in their own bubble. TTEC's
  // Oct 8 snippet adds this as a second setCustomCss call, which would replace
  // every rule above, so it lives in this one string instead.
  '[data-selector="CUSTOMER_MESSAGE_BUBBLE"] a { color: #fff !important; text-decoration: underline !important; }';

let loadPromise = null;

// Injects the CXone loader once and queues its setup calls (the cxone stub
// buffers them until the module arrives). Resolves false if the script fails.
export function loadNiceChat() {
  if (loadPromise) return loadPromise;

  const { brandId, loaderUrl } = getNiceChatConfig();

  loadPromise = new Promise(resolve => {
    const name = 'cxone';
    window.CXoneDfo = name;
    window[name] =
      window[name] ||
      function() {
        (window[name].q = window[name].q || []).push(arguments);
      };
    window[name].u = loaderUrl;

    const script = document.createElement('script');
    script.type = 'module';
    script.src = `${loaderUrl}?${Math.round(Date.now() / 1e3 / 3600)}`;
    script.onload = () => resolve(true);
    script.onerror = () => {
      console.error('[NiceChat] CXone loader failed to load:', loaderUrl);
      resolve(false);
    };
    document.head.appendChild(script);

    const cxone = window[name];
    cxone('init', brandId);
    cxone('guide', 'hidePreSurvey');
    cxone('chat', 'setCustomCss', CHAT_CSS);
    cxone('guide', 'init');
    // Our launcher replaces CXone's own round button.
    cxone(
      'guide',
      'setCustomCss',
      '[data-selector="GUIDE_CHANNEL_BUTTON"] { display: none !important; }'
    );
  });

  return loadPromise;
}

export function openNiceChat() {
  if (!isNiceChatEnabled() || typeof window.cxone !== 'function') return false;
  window.cxone('guide', 'openEntrypoint', getNiceChatConfig().entryPoint);
  return true;
}

// Test-only: forget the injected loader between tests.
export function resetNiceChatForTests() {
  loadPromise = null;
}
